import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import '../styles/admin-auth.css';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await signIn({ email, password });
    setLoading(false);

    if (error) {
      // Credentials are genuinely wrong
      setError('Invalid email or password. Please try again.');
      return;
    }

    // Credentials correct — now check admin role
    // isAdmin is set inside signIn() via AdminAuthContext
    // Give state a moment to update then check
    setTimeout(() => {
      // Navigation handled by AdminAuthContext state change
      // If not admin, AdminProtectedRoute shows Access Denied screen
      navigate('/dashboard');
    }, 400);
  };

  return (
    <div className="admin-auth-wrapper">
      <div className="admin-auth-bg" />

      <div className="admin-auth-card">
        {/* Logo */}
        <div className="admin-logo">
          <svg width="28" height="24" viewBox="0 0 32 28" fill="none">
            <rect x="0" y="8" width="6" height="20" rx="1" fill="#E8471E"/>
            <rect x="9" y="4" width="6" height="24" rx="1" fill="#E8471E"/>
            <rect x="18" y="0" width="6" height="28" rx="1" fill="#E8471E"/>
            <rect x="26" y="6" width="6" height="22" rx="1" fill="#C13010"/>
          </svg>
          <span className="admin-logo-text">SYNFIN</span>
          <span className="admin-badge">ADMIN</span>
        </div>

        <h1 className="admin-auth-title">Admin Portal</h1>
        <p className="admin-auth-subtitle">Restricted access — authorised personnel only</p>

        <div className="admin-security-notice">
          <Shield size={14} />
          <span>This portal is monitored and all access is logged</span>
        </div>

        {error && (
          <div className="admin-error">
            <AlertCircle size={15} />{error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="admin-form-group">
            <label className="admin-form-label">Admin email</label>
            <input
              type="email" className="admin-form-input"
              placeholder="admin@synfin.ca"
              value={email} onChange={e => setEmail(e.target.value)}
              required maxLength={254} autoComplete="email"
            />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Password</label>
            <div className="admin-input-icon-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="admin-form-input"
                placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)}
                required maxLength={128} autoComplete="current-password"
              />
              <button type="button" className="admin-icon-btn"
                onClick={() => setShowPassword(v => !v)}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" className="admin-btn-primary" disabled={loading}>
            {loading ? <span className="admin-spinner" /> : (
              <><Shield size={16} /> Sign in to Admin Portal</>
            )}
          </button>
        </form>
      </div>

      <p className="admin-auth-footer">
        SynFin Technologies Inc. — Admin Portal v1.0 Alpha
      </p>
    </div>
  );
};

export default AdminLoginPage;
